/**
 * Game Feel Polish Phase 3 (Audio) — every sound in Tallow is synthesized at
 * runtime via the Web Audio API (`engine/audio/synth.ts`); no sample/asset
 * files exist for audio anywhere in this repo, unlike the KayKit character
 * packs. Kept in one tuning file per this codebase's convention (see
 * `character-visuals-tuning.ts`) so sound-design values aren't buried in
 * `audio-engine.ts`'s own logic.
 */

/** Default bus gains, 0-1. No settings UI exists yet — these are the only volume knobs until one is built. */
export const AUDIO_GAINS = {
  MASTER: 0.8,
  SFX: 0.9,
  AMBIENT: 0.35,
  UI: 0.6,
  /**
   * Music sits well under everything else on purpose. The beds are mastered to
   * -20 LUFS (`tools/master-music.sh`) where the synthesized cues are peak-normalized,
   * so this is quieter than the number alone suggests -- which is the intent: music is
   * the only sound in this game a player never needs to *hear* to play correctly.
   */
  MUSIC: 0.45,
};

/** Music cross-screen fades. Long enough not to read as a cut, short enough that leaving the hub feels immediate. */
export const MUSIC_FADE = {
  IN_S: 1.6,
  OUT_S: 0.9,
};

/**
 * Music beds, by screen.
 *
 * There is deliberately **no dungeon entry**. `design/gdd/game-concept.md` makes
 * hearing the load-bearing skill -- "every fight you win tells three other people
 * exactly where you are", no minimap, no nameplates, audio literacy named as a
 * core Discovery aesthetic. A music bed over that masks the exact cues the game
 * asks players to read, so the dungeon keeps only its per-stratum ambient bed
 * (`AMBIENT_PROFILES`). Music plays where there is nothing to listen *for*.
 */
export const MUSIC_TRACKS = {
  HUB: '/audio/hub-sconce.m4a',
} as const;

/**
 * Recorded one-shot cues, by id.
 *
 * Every entry here has a synthesized fallback still wired behind it (see
 * `SampleBank.play`'s contract) — a sample that is missing or still decoding
 * degrades to the oscillator cue, never to silence. That is what lets
 * `design/gdd/audio.md`'s phases land one sound at a time.
 */
export const SFX = {
  LEVEL_UP: '/audio/sfx/level-up.m4a',
} as const;

const sfx = (name: string) => `/audio/sfx/${name}.m4a`;

/**
 * Footsteps, by stratum, as interchangeable takes.
 *
 * Keyed by `design/gdd/stratum.md`'s 1=Cellars / 2=Ossuary / 3=Vaults, matching
 * `AMBIENT_PROFILES`. Material, not armour: four takes of one surface beats two
 * takes each of two armour weights, because an audible repeat is the worse
 * flaw, and weight can be applied at playback as pitch and gain where a
 * recorded take cannot be un-repeated. Per-armour banks belong with a later
 * pass if they earn their download.
 */
export const FOOTSTEP_SAMPLES: Record<number, readonly string[]> = {
  1: [sfx('step_cellars_1'), sfx('step_cellars_2'), sfx('step_cellars_3'), sfx('step_cellars_4')],
  2: [sfx('step_ossuary_1'), sfx('step_ossuary_2'), sfx('step_ossuary_3'), sfx('step_ossuary_4')],
  3: [sfx('step_vaults_1'), sfx('step_vaults_2'), sfx('step_vaults_3'), sfx('step_vaults_4')],
};

/**
 * Combat cues by `combat-resolver.ts`'s own outcome vocabulary, plus the two
 * flags that ride alongside it.
 *
 * Before this existed the client read `if (msg.outcome === 'hit')` and nothing
 * else — parry, block, dodge and miss were silent, which meant the mechanic the
 * concept doc names as the core of the 30-second loop ("parry-into-stagger")
 * made no sound at all. Parry is bright steel; block is a dull shield thud.
 * That difference is not decoration: it is how a third party listening from the
 * next room judges whether the fight is worth crashing.
 */
export const COMBAT_SAMPLES = {
  parried: [sfx('parry_1'), sfx('parry_2'), sfx('parry_3')],
  blocked: [sfx('block_1'), sfx('block_2'), sfx('block_3')],
  dodged: [sfx('dodge_1'), sfx('dodge_2')],
  miss: [sfx('swing_light'), sfx('swing_medium'), sfx('swing_heavy')],
  stagger: [sfx('stagger_1'), sfx('stagger_2')],
  riposte: [sfx('riposte_1'), sfx('riposte_2')],
  /** A body: players, and anything wearing flesh. */
  impactFlesh: [sfx('impact_flesh_1'), sfx('impact_flesh_2'), sfx('impact_flesh_3')],
  /** Skeletons — the entire bestiary, so this is the common case in PvE. */
  impactBone: [sfx('impact_bone_1'), sfx('impact_bone_2'), sfx('impact_bone_3')],
} as const;

/**
 * Ability casts, by `AbilityFxKind` archetype.
 *
 * SHOUT and LINK ship a single take: both second takes profiled as SCENE
 * (peak arriving 50-60% in) across two regenerations, and an ability on a
 * 6-45s cooldown does not repeat often enough for one take plus pitch jitter
 * to read as repetitive. See tools/sfx/phase2.tsv.
 */
export const ABILITY_CAST_SAMPLES: Record<string, readonly string[]> = {
  WARD: [sfx('cast_ward_1'), sfx('cast_ward_2')],
  SHROUD: [sfx('cast_shroud_1'), sfx('cast_shroud_2')],
  SHOUT: [sfx('cast_shout_1')],
  SWEEP: [sfx('cast_sweep_1'), sfx('cast_sweep_2')],
  STRIKE: [sfx('cast_strike_1'), sfx('cast_strike_2')],
  BOLT: [sfx('cast_bolt_1'), sfx('cast_bolt_2')],
  BLESSING: [sfx('cast_blessing_1'), sfx('cast_blessing_2')],
  SNARE: [sfx('cast_snare_1'), sfx('cast_snare_2')],
  LINK: [sfx('cast_link_1')],
};

/** The two UI pings, which were the most obviously synthetic sounds in the game. */
export const UI_SAMPLES = {
  LOOT: [sfx('ui_loot_1'), sfx('ui_loot_2')],
  REJECT: [sfx('ui_reject_1'), sfx('ui_reject_2')],
} as const;

/** Run and round outcomes. One take each — they play once, at the end. */
export const OUTCOME_SAMPLES: Record<string, string> = {
  EXTRACTED: sfx('run_extract'),
  DIED: sfx('run_died'),
  WIN: sfx('round_win'),
  DRAW: sfx('round_draw'),
  LOSE: sfx('round_lose'),
};

/**
 * Per-stratum ambience: one looping bed plus the intermittent one-shots that
 * punctuate it, replacing `AMBIENT_PROFILES`' oscillator drones and generated
 * noise events. Beds are generated with ElevenLabs' seamless loop mode, so the
 * masher's crossfade is skipped for them (`tools/master-music.sh` with xfade=0).
 *
 * **Beds are deliberately excluded from `ALL_SAMPLE_URLS`.** They are ~270KB
 * each and a run only ever visits one stratum, so eagerly preloading all three
 * would spend 800KB of a phone's connection to have two of them never play.
 * `AudioEngine.startAmbient` loads the one it needs.
 */
export const AMBIENT_SAMPLES: Record<number, { bed: string; events: readonly string[] }> = {
  1: { bed: sfx('amb_cellars'), events: [sfx('amb_drip_1'), sfx('amb_drip_2')] },
  2: { bed: sfx('amb_ossuary'), events: [sfx('amb_rattle_1'), sfx('amb_rattle_2')] },
  3: { bed: sfx('amb_vaults'), events: [sfx('amb_resonance_1'), sfx('amb_resonance_2')] },
};

/**
 * Playback gain per cue family.
 *
 * These are the mix. Footsteps sit lowest by a wide margin: they are the most
 * frequently played sound in the game by an order of magnitude, and audio.md
 * rule 2 requires perceived loudness to track the noise value the simulation
 * assigns — the design's own `SPRINT_AMBIENT_NOISE_RADIUS_M` is 6m against a
 * 12m weapon-hit radius, so a footstep must read as roughly half the event a
 * weapon hit is, not as its equal.
 */
export const COMBAT_GAINS = {
  FOOTSTEP: 0.30,
  /** A sprinting body is deliberately louder — that is the tell it is meant to be. */
  FOOTSTEP_SPRINT_MULTIPLIER: 1.5,
  PARRY: 0.75,
  BLOCK: 0.70,
  DODGE: 0.45,
  MISS: 0.40,
  STAGGER: 0.60,
  RIPOSTE: 0.80,
  IMPACT: 0.85,
  /** Casts are yours and your squad's, and they fire constantly in a fight. */
  ABILITY_CAST: 0.55,
  /** UI is not in the world; it competes with nothing and should not shout. */
  UI: 0.5,
  /** Run and round stings own the moment — nothing else is happening. */
  OUTCOME: 0.85,
  /** The bed. Under everything, always — audio.md rule 4. */
  AMBIENT_BED: 0.5,
  AMBIENT_EVENT: 0.45,
} as const;

/**
 * Pitch jitter, as a +/- fraction of 1.0 playback rate.
 *
 * `playHitImpact` already applies +/-10% to its synthesis for the same reason.
 * Four takes plus jitter reads as far more variety than four takes alone,
 * because the ear catches an identical *pitch* before it catches an identical
 * waveform.
 */
export const SAMPLE_PITCH_JITTER = 0.12;

/**
 * Minimum gap between two rejection cues.
 *
 * A player mashing an ability that is on cooldown generates one refusal per
 * keypress, and a live run measured **118 reject cues in about a minute** of
 * ordinary key-pressing — a machine-gun of thunks that says far less than one
 * thunk would. Rejections are the only cue in the game driven directly by how
 * fast a player can press a key, so this is the only one that needs a floor.
 *
 * 180ms is above a comfortable mash rate and well below the shortest real
 * cooldown, so two *meaningfully separate* refusals still both sound.
 */
export const UI_REJECT_MIN_INTERVAL_MS = 180;

/**
 * How far a footstep carries.
 *
 * 12m, matching the FOOTSTEP_MAX_AUDIBLE_M declared further down for the same
 * purpose. Note this comfortably exceeds the 10m lit / 4m dark sight radius the
 * server gates state on, so in practice the view gate is the real limit on
 * hearing another delver, not this number — see design/gdd/audio.md on why
 * `noise.heard` is the missing half of that.
 */
export const REMOTE_FOOTSTEP_MAX_AUDIBLE_M = 12;

/**
 * Per-cue playback gain, applied on top of the UI bus.
 *
 * A level-up fires mid-run, over combat. audio.md's rule 4 — never mask the
 * diagnostic layer — applies to it as much as to music: a player who cannot
 * hear footsteps for two seconds because they levelled up has been punished for
 * succeeding, and at exactly the moment they are distracted by a banner. The
 * cue is short (1.9s, decaying 32 dB across its length) and sits below full
 * scale for that reason.
 */
export const SFX_GAINS: Record<string, number> = {
  [SFX.LEVEL_UP]: 0.7,
};

/** Every recorded cue this game ships, for preloading. */
export const ALL_SAMPLE_URLS: readonly string[] = [
  ...Object.values(SFX),
  ...Object.values(FOOTSTEP_SAMPLES).flat(),
  ...Object.values(COMBAT_SAMPLES).flat(),
  ...Object.values(ABILITY_CAST_SAMPLES).flat(),
  ...Object.values(UI_SAMPLES).flat(),
  ...Object.values(OUTCOME_SAMPLES),
  // Ambient EVENTS are small and preload with everything else; the BEDS do not
  // — see AMBIENT_SAMPLES on why 800KB of unused strata is not worth eager loading.
  ...Object.values(AMBIENT_SAMPLES).flatMap((a) => a.events),
];

/** Manual stereo-pan + linear distance falloff (see `spatial-audio.ts`) — simpler than a full 3D PannerNode graph, sufficient for this game's fixed over-the-shoulder camera. */
export const HIT_SOUND_MAX_AUDIBLE_M = 20;
export const PAN_REFERENCE_M = 4; // full hard pan by this many meters off to one side of the camera's facing

/** melee-combat.md's weapon damage table tops out around 52 (HEAVY); riposte/crit/talent multipliers can push it higher, so this is a soft ceiling, not a hard cap — clamped in `computeHitIntensity`. */
export const HIT_INTENSITY_DAMAGE_NORMALIZER = 60;
export const HIT_INTENSITY_MIN = 0.15; // even a Light tap should read as *something*, not silence

/** Stride cadence — sprint noticeably faster than walk, same spirit as the weapon-timing tables elsewhere in this config directory. */
export const FOOTSTEP_TIMING = { walkIntervalMs: 420, sprintIntervalMs: 260 };
export const FOOTSTEP_MAX_AUDIBLE_M = 12; // reserved for a future remote-footstep pass; local footsteps don't need distance falloff (they're always "at the camera")

/**
 * Per-stratum ambient bed profile (`stratum-palette.ts`'s own 1=Cellars/
 * 2=Ossuary/3=Vaults convention, backend/src/game/shared/stratum.ts). Chosen
 * for the same narrative progression as the visual palette: damp and low
 * -> colder and thinner -> deep and resonant.
 */
export interface AmbientProfile {
  droneHzs: number[];
  noiseCutoffHz: number;
  eventIntervalRangeMs: [number, number];
  eventType: 'drip' | 'rattle' | 'resonance';
}

export const AMBIENT_PROFILES: Record<number, AmbientProfile> = {
  1: { droneHzs: [45, 67], noiseCutoffHz: 400, eventIntervalRangeMs: [4000, 9000], eventType: 'drip' },
  2: { droneHzs: [52, 78], noiseCutoffHz: 900, eventIntervalRangeMs: [5000, 11000], eventType: 'rattle' },
  3: { droneHzs: [38, 58, 96], noiseCutoffHz: 250, eventIntervalRangeMs: [6000, 13000], eventType: 'resonance' },
};

/**
 * The level-up fanfare (2026-08-25).
 *
 * A major arpeggio that arrives *rising* and then rings — the shape WoW's own
 * level-up cue uses, and the reason it reads as an achievement rather than as
 * a notification. Written as data rather than a sequence of `playTone` calls so
 * the interval structure is legible and tunable in one place.
 *
 * C5-E5-G5-C6, i.e. a plain major triad plus the octave. Deliberately the most
 * consonant thing available: every other synthesized cue in this game is either
 * percussive (hits, footsteps) or a two-note ping (loot, reject), so a full
 * chord is unmistakably not one of them, without needing to be loud.
 *
 * The `hold` layer is the ring: the root and the octave sustained under the
 * arpeggio at low gain, so the fanfare has a tail instead of stopping dead.
 */
export const LEVEL_UP_FANFARE = {
  /** Ascending, each `stepS` after the last. */
  ARPEGGIO_HZ: [523.25, 659.25, 783.99, 1046.5],
  STEP_S: 0.085,
  NOTE_DECAY_S: 0.42,
  NOTE_PEAK_GAIN: 0.24,
  /** The sustained pad under it: root and octave, entering with the first note. */
  HOLD_HZ: [261.63, 523.25],
  HOLD_DECAY_S: 1.5,
  HOLD_PEAK_GAIN: 0.09,
  /** A soft airy sweep on top, for the shimmer. */
  SPARKLE_HZ: 1568,
  SPARKLE_DELAY_S: 0.22,
  SPARKLE_DECAY_S: 0.7,
  SPARKLE_PEAK_GAIN: 0.07,
} as const;

/**
 * Visualizing abilities (2026-08-26) — what an ability sounds like.
 *
 * Before this, every ability in the game played `playUiLoot()` — the same
 * two-note blip a picked-up coin makes — and only for the player who pressed
 * it. A squadmate's Warcry, a rival's Trinket and a druid's Mend were all
 * silent, which is a real loss in a game where you often cannot see your
 * party: hearing a heal go off behind you is how you know someone has your
 * back.
 *
 * One profile per effect archetype (`config/ability-vfx.ts`'s
 * `AbilityFxKind`), not one per ability, matching how the effects themselves
 * are grouped. Every profile is two layers — a tone and a noise burst —
 * because that is the shape every other cue in this game already uses, and a
 * third layer costs a voice for detail nobody hears mid-fight.
 *
 * `tone.sweepHz` is where the note *ends*: a rising sweep reads as something
 * being raised or summoned, a falling one as something released or landing.
 * That single sign is doing most of the characterisation work here.
 */
export interface AbilityCastProfile {
  tone: {
    freq: number;
    /** Where the pitch lands by the end. Equal to `freq` for a flat note. */
    sweepHz: number;
    type: OscillatorType;
    decayS: number;
    peakGain: number;
  };
  noise: {
    filterType: BiquadFilterType;
    filterFreq: number;
    durationS: number;
    decayS: number;
    peakGain: number;
  };
}

export const ABILITY_CAST_PROFILES: Record<string, AbilityCastProfile> = {
  // A shield closing: metallic, rising, brief.
  WARD: {
    tone: { freq: 320, sweepHz: 520, type: 'triangle', decayS: 0.3, peakGain: 0.3 },
    noise: { filterType: 'highpass', filterFreq: 2_200, durationS: 0.12, decayS: 0.08, peakGain: 0.18 },
  },
  // Going to ground: a falling breath, nothing tonal to catch the ear.
  SHROUD: {
    tone: { freq: 400, sweepHz: 120, type: 'sine', decayS: 0.35, peakGain: 0.14 },
    noise: { filterType: 'lowpass', filterFreq: 1_400, durationS: 0.5, decayS: 0.3, peakGain: 0.3 },
  },
  // A bellow. The lowest and longest thing in the table, and the only one
  // that should carry across a room.
  SHOUT: {
    tone: { freq: 150, sweepHz: 96, type: 'sawtooth', decayS: 0.5, peakGain: 0.4 },
    noise: { filterType: 'lowpass', filterFreq: 900, durationS: 0.55, decayS: 0.32, peakGain: 0.35 },
  },
  // Steel through air: mostly noise, the tone only giving it a body.
  SWEEP: {
    tone: { freq: 260, sweepHz: 170, type: 'sawtooth', decayS: 0.16, peakGain: 0.22 },
    noise: { filterType: 'bandpass', filterFreq: 1_800, durationS: 0.25, decayS: 0.12, peakGain: 0.4 },
  },
  // A deliberate blow — sharper and higher than a weapon hit, so an ability
  // landing is distinguishable from a swing landing without looking.
  STRIKE: {
    tone: { freq: 200, sweepHz: 130, type: 'square', decayS: 0.12, peakGain: 0.28 },
    noise: { filterType: 'highpass', filterFreq: 1_600, durationS: 0.1, decayS: 0.06, peakGain: 0.3 },
  },
  // The one spell that flies: bright, and rising as it leaves the hand.
  BOLT: {
    tone: { freq: 620, sweepHz: 940, type: 'sine', decayS: 0.22, peakGain: 0.26 },
    noise: { filterType: 'highpass', filterFreq: 3_000, durationS: 0.14, decayS: 0.1, peakGain: 0.12 },
  },
  // A heal. The only consonant, unhurried cue in the set — it should feel
  // like relief, which in practice means slow attack and no transient.
  BLESSING: {
    tone: { freq: 523.25, sweepHz: 659.25, type: 'sine', decayS: 0.55, peakGain: 0.26 },
    noise: { filterType: 'highpass', filterFreq: 4_000, durationS: 0.3, decayS: 0.22, peakGain: 0.07 },
  },
  // Roots closing: low, woody, and unpleasant on purpose — it is happening to
  // someone.
  SNARE: {
    tone: { freq: 110, sweepHz: 74, type: 'sawtooth', decayS: 0.4, peakGain: 0.3 },
    noise: { filterType: 'bandpass', filterFreq: 500, durationS: 0.45, decayS: 0.28, peakGain: 0.26 },
  },
  // A taunt is a challenge shouted at one thing: brassy and flat, no sweep.
  LINK: {
    tone: { freq: 233, sweepHz: 233, type: 'square', decayS: 0.28, peakGain: 0.3 },
    noise: { filterType: 'bandpass', filterFreq: 1_100, durationS: 0.2, decayS: 0.14, peakGain: 0.16 },
  },
};

/** Beyond this, an ability cast is inaudible. Further than a hit carries — a Warcry is meant to be heard. */
export const ABILITY_SOUND_MAX_AUDIBLE_M = 26;
